import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ENTRY_TYPE, EntryRequest } from '../../../modules/EntryRequests/types';
import { DateTime } from 'luxon';
import dayjs from 'dayjs';

export const entryRequestValidationSchema = Yup.object({
  /* requeridos sin condicion */

  // Solicitante
  requester_id: Yup.string().required('Solicitante es requerido'),

  // Tipo de entrada
  entry_type: Yup.string().required('Tipo de entrada es requerido'),

  // Cantidad entregas estipuladas

  deliveries: Yup.number()
    .min(1, 'Debe haber al menos una factura')
    .max(9999999999, 'Longitud maxima 10')
    .required('Cantidad entregas estipuladas es requerido'),

  // Seccional entrada
  entry_section_id: Yup.string().required('Seccional entrada es requerido'),

  entry_requester_section_id: Yup.string().required(
    'Seccional entrada es requerido'
  ),
  // Tipo de bien
  item_type: Yup.string().required('Tipo de bien es requerido'),

  // Dependencia
  dependency_id: Yup.string().required('Dependencia es requerido'),

  // Entregas en otras seccionales
  other_deliver: Yup.string().required(
    'Entregas en otras seccionales es requerido'
  ),

  // Fecha de solicitud
  query_date: Yup.string().required('Fecha de solicitud es requerido'),

  // Número de factura
  invoice_number: Yup.string()
    .max(32, 'Máximo 32 caracteres')
    .required(
      'Número de: factura/oficio/peritaje/Doc. equivalente/Avalúo/Informe es requerido'
    ),

  // Fecha factura
  invoice_date: Yup.date()
    .nullable()
    .required(
      'Fecha de: factura/oficio/peritaje/Doc. equivalente/Avalúo/Informe es requerida'
    ),

  // Cantidad de facturas
  invoice_count: Yup.number()
    .min(1, 'Debe haber al menos una factura')
    .max(9999999999, 'Longitud maxima 10')
    .required('Cantidad de facturas es requerido'),

  //Supervisor
  supervisor: Yup.string().when('entry_type', {
    is: (value: any) =>
      !value ||
      value === ENTRY_TYPE.CONTRATO ||
      value === ENTRY_TYPE.ORDEN_COMPRA ||
      value === null,
    then: (schema) =>
      schema
        .required('Supervisor es requerido')
        .max(500, 'Máximo 500 caracteres'),
    otherwise: (schema) => schema.max(500, 'Máximo 500 caracteres').nullable(),
  }),

  //Observaciones
  observations: Yup.string().max(500, 'Máximo 500 caracteres'),

  /* requeridos con condicion */

  // Valor de la factura/ adquisición
  entry_value: Yup.string()
    .matches(
      /^\d{1,13}(\.\d{1,2})?$/,
      'Debe ser un número con máximo 15 caracteres y 2 decimales'
    )
    .when('entry_type', {
      is: (value: any) =>
        [
          ENTRY_TYPE.ORDEN_COMPRA,
          ENTRY_TYPE.CONTRATO,
          ENTRY_TYPE.COMISO,
          ENTRY_TYPE.DONACION,
          ENTRY_TYPE.COMODATO,
          ENTRY_TYPE.ACUERDO_INTERADMINISTRATIVO,
          ENTRY_TYPE.INCAUTADO_CON_FINES_DE_COMISO,
          ENTRY_TYPE.ORDEN_JUDICIAL,
        ].includes(value),
      then: (schema) =>
        schema.required('Valor de la factura/adquisición es requerido'),
      otherwise: (schema) => schema.nullable(),
    }),

  // Número de orden de compra
  purchase_order: Yup.string().when('entry_type', {
    is: ENTRY_TYPE.ORDEN_COMPRA,
    then: (schema) => schema.required('Número de orden de compra es requerido'),
    otherwise: (schema) => schema.nullable(),
  }),

  // Número de contrato
  contract_number: Yup.string().when('entry_type', {
    is: ENTRY_TYPE.CONTRATO,
    then: (schema) => schema.required('Número de contrato es requerido'),
    otherwise: (schema) => schema.nullable(),
  }),

  // Fecha inicio contrato
  contract_start_date: Yup.date()
    .nullable()
    .when('entry_type', {
      is: ENTRY_TYPE.CONTRATO,
      then: (schema) => schema.required('Fecha inicio contrato es requerido'),
      otherwise: (schema) => schema.nullable(),
    }),

  // Fecha final contrato
  contract_end_date: Yup.date()
    .nullable()
    .when('entry_type', {
      is: ENTRY_TYPE.CONTRATO,
      then: (schema) =>
        schema
          .required('Fecha final contrato es requerido')
          .min(
            Yup.ref('contract_start_date'),
            'El campo Fecha de finalización del contrato debe ser una fecha posterior o igual a Fecha de inicio del contrato.'
          ),
      otherwise: (schema) => schema.nullable(),
    }),

  // Número de Acuerdo interadministrativo
  interadmin_agreement_number: Yup.string().when('entry_type', {
    is: ENTRY_TYPE.ACUERDO_INTERADMINISTRATIVO,
    then: (schema) =>
      schema.required('Número de Acuerdo interadministrativo es requerido'),
    otherwise: (schema) => schema.nullable(),
  }),

  // Fecha inicial acuerdo interadministrativo
  interadmin_start_date: Yup.date().when('entry_type', {
    is: ENTRY_TYPE.ACUERDO_INTERADMINISTRATIVO,
    then: (schema) =>
      schema.required('Fecha inicial acuerdo interadministrativo es requerido'),
    otherwise: (schema) => schema.nullable(),
  }),

  // Fecha final acuerdo interadministrativo
  interadmin_end_date: Yup.date().when(
    ['entry_type', 'interadmin_start_date'],
    {
      is: (entryType: any, startDate: any) =>
        entryType === ENTRY_TYPE.ACUERDO_INTERADMINISTRATIVO && !!startDate,
      then: (schema) =>
        schema
          .min(
            Yup.ref('interadmin_start_date'),
            'La fecha de finalización debe ser posterior o igual a la fecha de inicio'
          )
          .required('Fecha final acuerdo interadministrativo es requerido'),
      otherwise: (schema) => schema.nullable(),
    }
  ),

  // Contacto NIT
  contact_nit: Yup.string()
    .matches(
      /^\d{4,18}-\d{1}$/,
      'Formato inválido. Ejemplo válido: 123456789-0'
    )
    .when('entry_type', {
      is: (value: any) =>
        [
          ENTRY_TYPE.DONACION,
          ENTRY_TYPE.COMISO,
          ENTRY_TYPE.COMODATO,
          ENTRY_TYPE.ACUERDO_INTERADMINISTRATIVO,
          ENTRY_TYPE.INCAUTADO_CON_FINES_DE_COMISO,
        ].includes(value),
      then: (schema) => schema.required('NIT es requerido'),
      otherwise: (schema) => schema.nullable(),
    }),

  // Contacto Nombre
  contact_name: Yup.string().when('entry_type', {
    is: (value: any) =>
      value === ENTRY_TYPE.DONACION ||
      value === ENTRY_TYPE.COMISO ||
      value === ENTRY_TYPE.COMODATO ||
      value === ENTRY_TYPE.ACUERDO_INTERADMINISTRATIVO ||
      value === ENTRY_TYPE.INCAUTADO_CON_FINES_DE_COMISO,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .notRequired()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value
        ),
  }),

  // Persona de contacto
  contact_person: Yup.string().when('entry_type', {
    is: (value: any) =>
      value === ENTRY_TYPE.DONACION ||
      value === ENTRY_TYPE.COMISO ||
      value === ENTRY_TYPE.COMODATO ||
      value === ENTRY_TYPE.ACUERDO_INTERADMINISTRATIVO ||
      value === ENTRY_TYPE.INCAUTADO_CON_FINES_DE_COMISO,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .notRequired()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value
        ),
  }),

  // Télefono de contacto
  contact_phone: Yup.string().when('entry_type', {
    is: (value: any) =>
      value === ENTRY_TYPE.DONACION ||
      value === ENTRY_TYPE.COMISO ||
      value === ENTRY_TYPE.COMODATO ||
      value === ENTRY_TYPE.ACUERDO_INTERADMINISTRATIVO ||
      value === ENTRY_TYPE.INCAUTADO_CON_FINES_DE_COMISO,
    then: (schema) => schema.required('Télefono de contacto es requerido'),
    otherwise: (schema) => schema.nullable(),
  }),

  // Número proceso juzgado/fiscalía/Noticia criminal
  decomiso_court_number: Yup.string().when('entry_type', {
    is: ENTRY_TYPE.INCAUTADO_CON_FINES_DE_COMISO,
    then: (schema) =>
      schema.required(
        'Número proceso juzgado/fiscalía/Noticia criminal es requerido'
      ),
    otherwise: (schema) => schema.nullable(),
  }),

  //Número de proceso decomiso
  decomiso_process_number: Yup.string().when('entry_type', {
    is: ENTRY_TYPE.INCAUTADO_CON_FINES_DE_COMISO,
    then: (schema) =>
      schema
        .max(32, 'Máximo 32 caracteres')
        .required('Número de proceso decomiso es requerido'),
    otherwise: (schema) => schema.nullable(),
  }),

  // Documento de donación
  donation_document: Yup.string().when('entry_type', {
    is: ENTRY_TYPE.DONACION,
    then: (schema) => schema.required('Documento de donación es requerido'),
    otherwise: (schema) => schema.nullable(),
  }),

  // Juzgado de conocimiento
  judgment_court_name: Yup.number().when('entry_type', {
    is: ENTRY_TYPE.COMISO,
    then: (schema) => schema.notRequired().nullable(),
    otherwise: (schema) => schema.nullable(),
  }),

  // Noticia criminal
  judgment_process_number: Yup.string()
    .max(50, 'Máximo 50 caracteres')
    .when('entry_type', {
      is: ENTRY_TYPE.COMISO,
      then: (schema) => schema.required('Noticia criminal es requerido'),
      otherwise: (schema) => schema.nullable(),
    }),

  // Número de contrato comodato
  comodato_contract_number: Yup.string()
    .trim()
    .when('entry_type', {
      is: ENTRY_TYPE.COMODATO,
      then: (schema) =>
        schema
          .required('Número de contrato comodato es requerido')
          .max(32, 'Máximo 32 caracteres'),
      otherwise: (schema) => schema.nullable(),
    }),

  // Fecha inicio de contrato comodato
  comodato_start_date: Yup.date()
    .nullable()
    .when('entry_type', {
      is: ENTRY_TYPE.COMODATO,
      then: (schema) =>
        schema.required('Fecha inicio de contrato comodato es requerido'),
      otherwise: (schema) => schema.nullable(),
    }),

  // Fecha final de contrato comodato
  comodato_end_date: Yup.date()
    .nullable()
    .when('entry_type', {
      is: ENTRY_TYPE.COMODATO,
      then: (schema) =>
        schema.required('Fecha final de contrato comodato es requerido'),
      otherwise: (schema) => schema.nullable(),
    }),
});

interface UseEntryRequestFormikProps {
  onSubmit: (values: EntryRequest) => void;
}

const useEntryRequestFormik = ({ onSubmit }: UseEntryRequestFormikProps) => {
  return useFormik<EntryRequest>({
    /* 
✅'requester_id="1"'                            -> Solicitante
✅'entry_type="Contrato"'                       -> Tipo de entrada
✅'deliveries="2"'                              -> Cantidad entregas estipuladas
✅'entry_section_id="1"'                        -> Seccional entrada
✅'item_type="1"'                               -> Tipo de bien
✅'dependency_id="1"'                           -> Dependencia
✅'other_deliver="1"'                           -> Entregas en otras seccionales
✅'query_date="2025-01-10"'                     -> Fecha de solicitud
✅'invoice_number="123456"'                     -> Número de factura
✅'invoice_date="2024-12-01"'                   -> Fecha factura
✅'invoice_count="1"'                           -> Cantidad de facturas
✅'supervisor="Supervisor"'                     -> supervisor
✅'entry_value="1234567"'                       -> Valor de la factura/ adquisición
✅'purchase_order="PO1234"'                     -> Número de orden de compra
✅'observations="Observaciones"'                ->  observations
✅'contract_number="123456"'                    -> Número de contrato
✅'contract_start_date="2024-12-01"'            -> Fecha inicio contrato
✅'contract_end_date="2024-12-12"'              -> Fecha final contrato
✅'donation_document="Documento 12345"'         -> Documento de donación
✅'judgment_court_name="Nombre Juzgado"'        ->  Número de Juzgado
✅'judgment_process_number="1234567"'           -> Número de proceso de sentencia
✅'comodato_contract_number="Comodato 12345"'   -> Número de contrato comodato
✅'comodato_start_date="2024-12-01"'            -> Fecha inicio de contrato comodato
✅'comodato_end_date="2024-12-12"'              -> Fecha final de contrato comodato
✅'interadmin_agreement_number="23654564"'      -> Número de Acuerdo interadministrativo
✅'interadmin_start_date="2024-12-01"'          -> Fecha inicial acuerdo interadministrativo 
✅'interadmin_end_date="2024-12-12"'            -> Fecha final acuerdo interadministrativo
✅'contact_nit="4165451"'                       -> Contacto NIT
✅'contact_name="Contacto"'                     -> Contacto Nombre
✅'contact_person="Contacto"'                   -> Persona de contacto
✅'contact_phone="123321"'                      -> Télefono de contacto
✅'documents[0]=@"/C:/Users/SCADA/Downloads/EF INICIAL ALMERIA.pdf"' -> Completed in EntryRequestCreateContainer
✅'delivery_section[0][section_id]="1"'         -> Completed in EntryRequestCreateContainer
✅'delivery_section[0][quantity]="3"'           -> Completed in EntryRequestCreateContainer
✅'delivery_section[0][invoice_number]="231212"'-> Completed in EntryRequestCreateContainer
✅'decomiso_court_number="1234566"'             -> Número Juzgado/Fiscalía
✅'decomiso_process_number="1234564"'           ->

*/

    initialValues: {
      requester_id: '',
      entry_type: '',
      invoice_count: 0,
      deliveries: 0,
      entry_section_id: '',
      item_type: '',
      dependency_id: '',
      other_deliver: '0',
      query_date: dayjs(),
      invoice_number: '0',
      invoice_date: '',
      entry_value: '',
      purchase_order: '',
      observations: '',
      contract_number: '',
      contract_start_date: '',
      contract_end_date: '',
      donation_document: '',
      judgment_court_name: '',
      judgment_process_number: '',
      comodato_contract_number: '',
      comodato_start_date: '',
      comodato_end_date: '',
      interadmin_agreement_number: '',
      interadmin_start_date: '',
      interadmin_end_date: '',
      contact_nit: '',
      contact_name: '',
      contact_person: '',
      contact_phone: '',
      decomiso_court_number: '',
      decomiso_process_number: '',
      entry_requester_section_id: '',
      supervisor: '',
      judgment_number_sentencia: null,
      numberInvoices: 1,
    },
    validationSchema: entryRequestValidationSchema,
    onSubmit,
  });
};

export default useEntryRequestFormik;
